import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { IoEyeOff, IoEye, IoLockClosedOutline, IoInformationCircleOutline } from "react-icons/io5";
import { MdAlternateEmail } from "react-icons/md";
import axios from "axios";
import { toast } from "react-toastify";
import { useUser } from "../context/UserContext";
import LoadingAnimation from "../components/LoadingAnimation";


function Login() {
  const [formData, setFormData] = useState({ useremail: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_URL;
  const { currentUser, setCurrentUser, loading } = useUser();

  useEffect(() => {
    if (!loading && currentUser != null) {
      setTimeout(() => {
        redirectUserByRole(currentUser);
      }, 0);
    }
  }, [currentUser, loading, navigate]);

  const redirectUserByRole = (user) => {
    if (user.firstLogin) {
      navigate("/changepassword");
      return;
    }

    switch (user.role) {
      case "ADMIN":
        navigate("/admin/dashboard");
        break;
      case "HR":
        navigate("/hr/dashboard");
        break;
      case "EMPLOYEE":
      case "MANAGER":
        navigate("/employee/dashboard");
        break;
      default:
        navigate("/unauthorized");
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate("/forgotpassword");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${baseUrl}/login`,
        {
          email: formData.useremail,
          password: formData.password,
        },
        { withCredentials: true }
      );

      const user = response.data;
      localStorage.setItem("token", user.token);
      setCurrentUser(user);

      toast.success("Login successful!", {
        position: "bottom-right",
        autoClose: 1000,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Invalid credentials!", {
          position: "bottom-right",
        });
      } else {
        toast.error("Login error: " + (error.response?.data || error.message), {
          position: "bottom-right",
        });
      }
      console.log("Login error: ", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <LoadingAnimation />;

  return (
    <div className="flex min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 90, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-400/20 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [90, 0, 90],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      {/* LEFT SECTION - Enhanced */}
      <div className="sm:w-1/2 w-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white flex flex-col justify-center items-center relative overflow-hidden sm:grid hidden">
        {/* Animated gradient overlay */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-purple-600/10 to-pink-600/10"
          animate={{
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:100px_100px]" />
        
        <div className="z-10 text-center px-8 max-w-lg">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <motion.div
              className="absolute inset-0 bg-white/10 blur-2xl rounded-full"
              animate={{
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            <img
              src="/companylogo.png"
              alt="Shri Padmavathi"
              className="h-20 w-auto mb-8 mx-auto bg-white/95 backdrop-blur-sm rounded-2xl px-4 py-2 shadow-2xl relative z-10"
            />
          </motion.div>

          <motion.h1
            className="text-6xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Welcome Back
          </motion.h1>

          <motion.p
            className="text-lg text-slate-300 leading-relaxed mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Sign in to access your personalized dashboard and manage your workspace
          </motion.p>

          <motion.div
            className="flex gap-6 justify-center items-center text-slate-400"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span className="text-sm">Secure Login</span>
            </div>
            <div className="w-px h-4 bg-slate-600" />
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span className="text-sm">24/7 Access</span>
            </div>
          </motion.div>
        </div>

        {/* Floating shapes */}
        <motion.div
          className="absolute top-20 left-20 w-32 h-32 border border-white/10 rounded-full"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "linear",
          }}
        />
        <motion.div
          className="absolute bottom-20 right-20 w-24 h-24 border border-white/10 rounded-lg"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -180, -360],
          }}
          transition={{
            duration: 12,
            repeat: Infinity,
            ease: "linear",
          }}
        />
      </div>

      {/* RIGHT SECTION - Enhanced Form */}
      <div className="sm:w-1/2 w-full flex justify-center items-center px-6 py-12 relative z-10">
        <motion.form
          onSubmit={handleLogin}
          className="bg-white/70 backdrop-blur-2xl p-10 sm:p-12 rounded-3xl shadow-2xl w-full max-w-md border border-white/20 relative overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          
          <div className="relative">
            <motion.h2
              className="text-4xl font-bold bg-gradient-to-r from-slate-900 via-slate-700 to-slate-900 bg-clip-text text-transparent mb-2"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Sign In
            </motion.h2>
            <motion.p
              className="text-slate-500 mb-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Enter your credentials to continue
            </motion.p>

            <div className="space-y-6">
              {/* Email Input - Floating Label */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <div className="relative">
                  <MdAlternateEmail
                    className={`absolute left-4 top-1/2 -translate-y-1/2 text-xl transition-all duration-300 ${
                      emailFocused || formData.useremail
                        ? "text-blue-500"
                        : "text-slate-400"
                    }`}
                  />
                  <input
                    type="email"
                    value={formData.useremail}
                    onChange={(e) =>
                      setFormData({ ...formData, useremail: e.target.value })
                    }
                    onFocus={() => setEmailFocused(true)}
                    onBlur={() => setEmailFocused(false)}
                    className="w-full border-2 border-slate-200 pl-12 pr-4 py-4 rounded-full focus:outline-none focus:border-blue-500 transition-all duration-300 bg-white/80 hover:bg-white peer"
                    placeholder=" "
                  />
                  <label
                    className={`absolute left-12 transition-all duration-300 pointer-events-none ${
                      emailFocused || formData.useremail
                        ? "-top-2.5 text-xs text-blue-500 bg-white px-2 left-10"
                        : "top-1/2 -translate-y-1/2 text-slate-400"
                    }`}
                  >
                    Email Address
                  </label>
                </div>
              </motion.div>

              {/* Password Input - Floating Label */}
              <motion.div
                className="relative"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <div className="relative">
                  <IoLockClosedOutline
                    className={`absolute left-4 top-1/2 -translate-y-1/2 text-xl transition-all duration-300 ${
                      passwordFocused || formData.password
                        ? "text-blue-500"
                        : "text-slate-400"
                    }`}
                  />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    className="w-full border-2 border-slate-200 pl-12 pr-12 py-4 rounded-full focus:outline-none focus:border-blue-500 transition-all duration-300 bg-white/80 hover:bg-white"
                    placeholder=" "
                  />
                  <label
                    className={`absolute left-12 transition-all duration-300 pointer-events-none ${
                      passwordFocused || formData.password
                        ? "-top-2.5 text-xs text-blue-500 bg-white px-2 left-10"
                        : "top-1/2 -translate-y-1/2 text-slate-400"
                    }`}
                  >
                    Password
                  </label>
                  <button
                    type="button"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-xl text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <IoEyeOff /> : <IoEye />}
                  </button>
                </div>
              </motion.div>
            </div>

            <motion.button
              type="submit"
              disabled={isSubmitting}
              className="w-full mt-8 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 hover:from-slate-900 hover:via-slate-800 hover:to-slate-900 text-white py-4 rounded-full transition-all duration-300 transform hover:shadow-xl hover:shadow-slate-500/50 relative overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100"
              whileHover={!isSubmitting ? { scale: 1.02 } : {}}
              whileTap={!isSubmitting ? { scale: 0.98 } : {}}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              <span className="relative z-10 font-semibold text-lg flex items-center justify-center gap-2">
                {isSubmitting ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing In...
                  </>
                ) : (
                  "Sign In"
                )}
              </span>
              {!isSubmitting && (
                <motion.div
                  className="absolute inset-0  opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={false}
                />
              )}
            </motion.button>

            <motion.div
              className="mt-6 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7 }}
            >
              <p className="text-sm text-slate-600">
                Forgot your password?{" "}
                <motion.span
                  onClick={handleForgotPassword}
                  className="text-blue-600 font-semibold cursor-pointer hover:text-blue-700 transition-colors"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Reset here
                </motion.span>
              </p>
            </motion.div>
          </div>
        </motion.form>
      </div>

      {/* INFO ICON - Enhanced */}
      <motion.div
        className="absolute top-6 right-6 z-20"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.8 }}
      >
        <motion.button
          onClick={() => setShowInfo(true)}
          className="bg-white/80 backdrop-blur-sm p-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 text-slate-600 hover:text-slate-800 border border-white/50"
          whileHover={{ scale: 1.1, rotate: 90 }}
          whileTap={{ scale: 0.9 }}
        >
          <IoInformationCircleOutline className="text-2xl" />
        </motion.button>
      </motion.div>

      {/* INFO POPUP - Enhanced */}
      {showInfo && (
        <motion.div
          className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowInfo(false);
            }
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            className="bg-white/90 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full mx-4 relative shadow-2xl border border-white/50"
          >
            <motion.button
              onClick={() => setShowInfo(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition-colors bg-slate-100 hover:bg-slate-200 w-8 h-8 rounded-full flex items-center justify-center"
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
            >
              ✕
            </motion.button>

            <motion.h3
              className="text-2xl font-bold bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent mb-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              About
            </motion.h3>

            <div className="space-y-6">
              <motion.div
                className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-2xl border border-blue-100"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h4 className="text-sm font-semibold text-slate-500 mb-1 uppercase tracking-wide">
                  Version
                </h4>
                <p className="text-2xl font-bold text-slate-800">2.1.3</p>
              </motion.div>

              <motion.div
                className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-2xl border border-purple-100"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h4 className="text-sm font-semibold text-slate-500 mb-2 uppercase tracking-wide">
                  Connect with us
                </h4>
                <a
                  href="mailto:hr.payroll@shripadmavathi.com"
                  className="text-blue-600 hover:text-blue-700 transition-colors font-medium inline-flex items-center gap-2 group"
                >
                  <span>hr.payroll@shripadmavathi.com</span>
                  <motion.span
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    initial={{ x: -10 }}
                    whileHover={{ x: 0 }}
                  >
                    →
                  </motion.span>
                </a>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}

export default Login;